import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { Typography } from '@material-ui/core'
import { ExportSection } from 'api/graphQL/mutations/export.mutations'
import { GetSupportingDocuments } from 'api/graphQL/queries/supportingDocs.queries'
import { setCopyProductTypeTrigger } from 'api/graphQL/reactiveVars/workspaceActions'
import {
    contentAreaLoading,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import {
    ActionButton,
    ActionButtons,
    ExtendedSvgIcon,
} from 'components/ActionBar'
import { useEditableContentActionBarStyles } from 'components/ActionBar/EditableContentActionBar'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { useCallback, useState } from 'react'
import { openCenteredBrowserWindow } from 'shared/utils/browserUtils'
import { base64toBlob } from 'shared/utils/fileUtils'
import {
    WorkspacePrintPreview,
    getPrintPreviewUrl,
    isClassicWorkspaceMode,
} from 'shared/utils/workspaceUtils'
import DuplicateProductTypeButton from './DuplicateProductTypeButton'
import { tryGetShortcutKey } from './PropertyEditor/propertyEditorUtils'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
} from './constants'

export interface PreviewProjectBarProps {
    selectedProjectElementNode: ProjectElementNode | undefined
    projectId: string
    isReviewer?: boolean
    isExportButtonDisabled?: boolean
    isStudioManagement?: boolean
    isOMWorkspace?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    isContentTeamFromStudioPage?: boolean
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
}

const ProjectBar: React.FC<PreviewProjectBarProps> = ({
    selectedProjectElementNode,
    projectId,
    isExportButtonDisabled,
    isStudioManagement,
    isOMWorkspace,
    selectedTopic,
    isContentTeamFromStudioPage,
    insertNewProductTypeElement,
    isReviewer,
}) => {
    const {
        actionButtonGroup,
        roundContainerActionBar,
        expandActionBar,
    } = useEditableContentActionBarStyles()
    const [isExportLoading, setExportLoading] = useState(false)

    const showDuplicateProductTypeButton =
        processEnv.REACT_APP_ShowDuplicateProductTypeButton === '1' &&
        selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
        !isContentTeamFromStudioPage

    const disableDuplicateProductTypeButton = !selectedProjectElementNode?.isInProject

    const isCopyProductTypeDisabled =
        !!!showDuplicateProductTypeButton ||
        isContentTeamFromStudioPage ||
        disableDuplicateProductTypeButton

    const { projectProductFamilyId } = useReactiveVar(workspaceContents)
    const { isContentAreaLoading } = useReactiveVar(contentAreaLoading)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [exportSection] = useMutation(ExportSection, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            const errorMessage = data?.exportProjectProductFamily?.errorMessage
            if (errorMessage) {
                showGlobalSnackbar({
                    variables: {
                        message: errorMessage,
                        variant: 'error',
                        id: 'ExportFailed',
                    },
                })
                setExportLoading(false)
                return
            }
            navigateToExternalLink(
                data?.exportProjectProductFamily?.downloadURL,
                true
            )
            setTimeout(() => {
                setExportLoading(false)
            }, 1000)
        },
        onError: (err) => {
            setExportLoading(false)
            showGlobalSnackbar({
                variables: {
                    message: err.message,
                    variant: 'error',
                    id: 'ExportFailed',
                },
            })
        },
    })

    const [
        getSupportingDocuments,
        { loading: isSupportingDocumentsLoading },
    ] = useLazyQuery(GetSupportingDocuments, {
        fetchPolicy: 'no-cache',
        onCompleted: async (data) => {
            try {
                const fileAsBase64 = await data?.getSupportingDocuments
                    ?.supportingDocument
                const fileAsPdfBlob = base64toBlob(fileAsBase64)
                var fileURL = URL.createObjectURL(fileAsPdfBlob)

                if (fileURL) {
                    window?.open(fileURL, '_blank')?.focus()
                }
            } catch (error) {
                console.warn(error)
                showGlobalSnackbar({
                    variables: {
                        message: 'Error fetching Supporting Documents.',
                        variant: 'error',
                        id: 'getSupportingDocumentsFailed',
                    },
                })
            }
        },
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        err,
                        'Error fetching Supporting Documents.'
                    ),
                    variant: 'error',
                    id: 'getSupportingDocumentsFailed',
                },
            })
        },
    })

    const noOp = useCallback(() => {}, [])

    const isExportDisabled = React.useMemo(
        () =>
            isExportButtonDisabled ||
            isReviewer ||
            (!isStudioManagement && !selectedProjectElementNode?.isInProject),
        [isStudioManagement, selectedProjectElementNode, isExportButtonDisabled]
    )

    const isPreviewDisabled = React.useMemo(
        () => !isStudioManagement && !selectedProjectElementNode?.isInProject,
        [isStudioManagement, selectedProjectElementNode]
    )

    const handleViewPrintPreview = React.useCallback(() => {
        if (selectedProjectElementNode?.elementId && !isStudioManagement) {
            openCenteredBrowserWindow(
                getPrintPreviewUrl({
                    projectId,
                    elementNode: selectedTopic ?? selectedProjectElementNode,
                    projectProductFamilyId,
                    isClassicWorkspace: isClassicWorkspaceMode(isStudioManagement, isOMWorkspace),
                } as WorkspacePrintPreview),
                `Preview${projectId}`,
                'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes'
            )
        } else if (isStudioManagement) {
            openCenteredBrowserWindow(
                getPrintPreviewUrl({
                    projectId,
                    elementNode: selectedProjectElementNode,
                    projectProductFamilyId,
                    isStudioManagement,
                } as WorkspacePrintPreview),
                `Preview${projectId}`,
                'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes'
            )
        }
    }, [
        projectId,
        selectedTopic,
        selectedProjectElementNode,
        projectProductFamilyId,
        isStudioManagement,
        isOMWorkspace,
    ])

    const handleExport = React.useCallback(() => {
        if (
            selectedProjectElementNode?.elementId ||
            (isStudioManagement && selectedProjectElementNode?.baseElementId)
        ) {
            setExportLoading(true)
            exportSection({
                variables: {
                    exportSectionInput: {
                        projectId,
                        projectProductFamilyId: isStudioManagement
                            ? selectedProjectElementNode.elementId
                                ? selectedProjectElementNode.elementId
                                : selectedProjectElementNode.baseElementId
                            : projectProductFamilyId
                            ? projectProductFamilyId
                            : selectedProjectElementNode.elementId
                            ? selectedProjectElementNode.elementId
                            : selectedProjectElementNode.baseElementId,
                        nodeId: selectedProjectElementNode?.id,
                        familyTreePath:
                            selectedProjectElementNode?.elementType ===
                            ProjectElementType.PRODUCTFAMILY
                                ? selectedProjectElementNode?.treePath
                                : selectedProjectElementNode?.parentTreePath,
                        isClassicWorkspace:
                            isClassicWorkspaceMode(isStudioManagement, isOMWorkspace) ?
                            true : undefined,
                    },
                },
            })
        }
    }, [
        projectId,
        selectedProjectElementNode,
        isStudioManagement,
        isOMWorkspace,
        projectProductFamilyId,
        exportSection,
        isExportLoading,
    ])

    const handleViewSupportingDocs = React.useCallback(() => {
        getSupportingDocuments({
            variables: {
                getSupportingDocsInput: {
                    productFamilyId: projectProductFamilyId,
                },
            },
        })
    }, [projectProductFamilyId])

    // handles project bar shortcut keys
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (
                tryGetShortcutKey('Export', event) &&
                !isExportDisabled &&
                !isContentAreaLoading
            ) {
                handleExport()
            } else if (
                tryGetShortcutKey('Preview', event) &&
                !isPreviewDisabled &&
                !isContentAreaLoading
            ) {
                handleViewPrintPreview()
            } else if (
                tryGetShortcutKey('SupportingDocs', event) &&
                !isSupportingDocumentsLoading &&
                !isContentAreaLoading
            ) {
                handleViewSupportingDocs()
            } else if (
                tryGetShortcutKey('CopyPT', event) &&
                !isCopyProductTypeDisabled &&
                !isContentAreaLoading
            ) {
                setCopyProductTypeTrigger({ isCopyProductTypeTriggered: true })
            }
        },
        [
            isExportDisabled,
            isContentAreaLoading,
            handleExport,
            isPreviewDisabled,
            handleViewPrintPreview,
            isSupportingDocumentsLoading,
            handleViewSupportingDocs,
            isCopyProductTypeDisabled,
            setCopyProductTypeTrigger,
        ]
    )

    const { SupportingDocs, Export, Preview, CopyPT } = ShortcutKeys
    // NOTE: Add/Update button props as necessary for future enhancement requests.
    // Also, order of action button matters when rendering
    const WORKSPACE_ALWAYS_AVAILABLE_ACTION_BUTTONS: Partial<ActionButtons> = {
        SupportingDocuments: {
            tooltip: {
                label: 'Supporting Documents',
                smallLabel: SupportingDocs,
            },
            variant: ActionButtonVariant.SupportingDocuments,
            disabled: isSupportingDocumentsLoading,
            loading: isSupportingDocumentsLoading,
            onClick: isSupportingDocumentsLoading
                ? noOp
                : handleViewSupportingDocs,
            svgIcon: (
                <ExtendedSvgIcon viewBox="-2 -4 28 28">
                    <path d="M10.6 16.05L17.65 9L16.25 7.6L10.6 13.25L7.75 10.4L6.35 11.8L10.6 16.05ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H9.2C9.41667 2.4 9.77917 1.91667 10.2875 1.55C10.7958 1.18333 11.3667 1 12 1C12.6333 1 13.2042 1.18333 13.7125 1.55C14.2208 1.91667 14.5833 2.4 14.8 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V5H5V19ZM12 4.25C12.2167 4.25 12.3958 4.17917 12.5375 4.0375C12.6792 3.89583 12.75 3.71667 12.75 3.5C12.75 3.28333 12.6792 3.10417 12.5375 2.9625C12.3958 2.82083 12.2167 2.75 12 2.75C11.7833 2.75 11.6042 2.82083 11.4625 2.9625C11.3208 3.10417 11.25 3.28333 11.25 3.5C11.25 3.71667 11.3208 3.89583 11.4625 4.0375C11.6042 4.17917 11.7833 4.25 12 4.25Z" />
                </ExtendedSvgIcon>
            ),
        },
        Export: {
            tooltip: {
                label: 'Export',
                smallLabel: Export,
            },
            variant: ActionButtonVariant.Export,
            disabled: isExportDisabled,
            onClick: isExportDisabled ? noOp : handleExport,
            loading: isExportLoading,
            svgIcon: (
                <ExtendedSvgIcon viewBox="-2 -2 28 28">
                    <path d="M6 23C5.45 23 4.97917 22.8042 4.5875 22.4125C4.19583 22.0208 4 21.55 4 21V10C4 9.45 4.19583 8.97917 4.5875 8.5875C4.97917 8.19583 5.45 8 6 8H9V10H6V21H18V10H15V8H18C18.55 8 19.0208 8.19583 19.4125 8.5875C19.8042 8.97917 20 9.45 20 10V21C20 21.55 19.8042 22.0208 19.4125 22.4125C19.0208 22.8042 18.55 23 18 23H6ZM11 16V4.825L9.4 6.425L8 5L12 1L16 5L14.6 6.425L13 4.825V16H11Z" />
                </ExtendedSvgIcon>
            ),
        },
        Preview: {
            tooltip: {
                label: 'Preview',
                smallLabel: Preview,
            },
            variant: ActionButtonVariant.Preview,
            disabled: isPreviewDisabled,
            onClick: isPreviewDisabled ? noOp : handleViewPrintPreview,
            svgIcon: (
                <ExtendedSvgIcon viewBox="-3 -3 28 28">
                    <path d="M11.45 17C11.75 17 12.0458 16.9625 12.3375 16.8875C12.6292 16.8125 12.9 16.7 13.15 16.55L15.6 19L17 17.6L14.55 15.15C14.7 14.9 14.8125 14.6292 14.8875 14.3375C14.9625 14.0458 15 13.75 15 13.45C15 12.4833 14.6583 11.6667 13.975 11C13.2917 10.3333 12.4667 10 11.5 10C10.5333 10 9.70833 10.3417 9.025 11.025C8.34167 11.7083 8 12.5333 8 13.5C8 14.4667 8.33333 15.2917 9 15.975C9.66667 16.6583 10.4833 17 11.45 17ZM11.5 15C11.0833 15 10.7292 14.8542 10.4375 14.5625C10.1458 14.2708 10 13.9167 10 13.5C10 13.0833 10.1458 12.7292 10.4375 12.4375C10.7292 12.1458 11.0833 12 11.5 12C11.9167 12 12.2708 12.1458 12.5625 12.4375C12.8542 12.7292 13 13.0833 13 13.5C13 13.9167 12.8542 14.2708 12.5625 14.5625C12.2708 14.8542 11.9167 15 11.5 15ZM6 22C5.45 22 4.97917 21.8042 4.5875 21.4125C4.19583 21.0208 4 20.55 4 20V4C4 3.45 4.19583 2.97917 4.5875 2.5875C4.97917 2.19583 5.45 2 6 2H14L20 8V20C20 20.55 19.8042 21.0208 19.4125 21.4125C19.0208 21.8042 18.55 22 18 22H6ZM13 9V4H6V20H18V9H13Z" />
                </ExtendedSvgIcon>
            ),
        },
        CopyProductType: {
            tooltip: {
                label: 'Copy Product Type',
                smallLabel: CopyPT,
            },
            variant: ActionButtonVariant.CopyProductType,
            disabled: isCopyProductTypeDisabled,
            isHidden: !showDuplicateProductTypeButton,
            onClick: noOp,
            svgIcon: (
                <ExtendedSvgIcon viewBox="-3 -4 28 28">
                    <path d="M5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20H5V22ZM3 18.5V16.5H5V18.5H3ZM3 15V13H5V15H3ZM3 11.5V9.5H5V11.5H3ZM3 8C3 7.45 3.19583 6.97917 3.5875 6.5875C3.97917 6.19583 4.45 6 5 6V8H3ZM6.5 22V20H8.5V22H6.5ZM9 18C8.45 18 7.97917 17.8042 7.5875 17.4125C7.19583 17.0208 7 16.55 7 16V4C7 3.45 7.19583 2.97917 7.5875 2.5875C7.97917 2.19583 8.45 2 9 2H18C18.55 2 19.0208 2.19583 19.4125 2.5875C19.8042 2.97917 20 3.45 20 4V16C20 16.55 19.8042 17.0208 19.4125 17.4125C19.0208 17.8042 18.55 18 18 18H9ZM9 16H18V4H9V16ZM10 22V20H12V22H10ZM13.5 22V20H15.5C15.5 20.55 15.3042 21.0208 14.9125 21.4125C14.5208 21.8042 14.05 22 13.5 22Z" />
                </ExtendedSvgIcon>
            ),
        },
    }

    // register keydown event listener and clean it up on unmount
    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    if (!selectedProjectElementNode) {
        return null
    }

    return (
        <Typography
            className={`${roundContainerActionBar} ${expandActionBar}`}
            component="div"
        >
            <div className={actionButtonGroup}>
                {Object.values(WORKSPACE_ALWAYS_AVAILABLE_ACTION_BUTTONS).map(
                    (props, idx) => {
                        return (
                            <>
                                {props.variant ===
                                ActionButtonVariant.CopyProductType ? (
                                    <DuplicateProductTypeButton
                                        selectedProjectElementNode={
                                            selectedProjectElementNode
                                        }
                                        projectProductFamilyId={
                                            projectProductFamilyId
                                        }
                                        insertNewProductTypeElement={
                                            insertNewProductTypeElement
                                        }
                                        actionButtonProps={props}
                                    />
                                ) : (
                                    <ActionButton key={idx} {...props} />
                                )}
                            </>
                        )
                    }
                )}
            </div>
        </Typography>
    )
}

export default React.memo(ProjectBar)
